import { useDoc } from '@/store/documentStore'
import { toast, useFeedback } from '@/store/feedbackStore'
import { useUi } from '@/store/uiStore'
import type { PageRef } from '@/types'
import {
  canShareFiles,
  download,
  ensurePdfName,
  pickPdfFiles,
  printPdf,
  saveAs as saveAsFile,
  shareFile,
  verifyWritePermission,
  withSuffix,
  writeToHandle,
} from './files'
import type { RasterPage } from './pdf/export'
import { isPdf, PasswordRequiredError } from './pdf/pdfjs'
import { allSources, getSource } from './pdf/sources'
import { deleteSession, loadSession, requestPersistence, storeHandle, storeSourceBytes } from './storage'

/** Open a PDF, prompting for a password when the file needs one. */
export async function openDocument(
  bytes: Uint8Array,
  name: string,
  handle: FileSystemFileHandle | null = null,
) {
  if (!isPdf(bytes)) {
    toast(`“${name}” doesn't look like a PDF.`, 'error')
    return false
  }
  let password: string | undefined
  let incorrect = false
  const fb = useFeedback.getState()
  for (;;) {
    try {
      fb.setBusy({ label: `Opening ${name}…` })
      await useDoc.getState().open({ bytes, name, handle, password })
      break
    } catch (e) {
      fb.setBusy(null)
      if (e instanceof PasswordRequiredError) {
        const pw = await fb.askPassword(name, incorrect || e.incorrect)
        if (pw === null) {
          useDoc.getState().setStatus('empty')
          return false
        }
        password = pw
        incorrect = true
        continue
      }
      console.error(e)
      useDoc.getState().setStatus('error', (e as Error).message)
      toast(`Couldn't open “${name}”: ${(e as Error).message}`, 'error')
      return false
    } finally {
      fb.setBusy(null)
    }
  }
  const doc = useDoc.getState()
  useUi.getState().select([])
  useUi.getState().setOrganize(false)
  useUi.getState().setCurrentPage(0)
  void requestPersistence()
  if (doc.sessionId) {
    for (const s of allSources()) await storeSourceBytes(doc.sessionId, s.info.id, s.bytes)
    await storeHandle(doc.sessionId, handle)
  }
  return true
}

export async function openFromPicker() {
  const [file] = await pickPdfFiles(false)
  if (file) await openDocument(file.bytes, file.name, file.handle)
}

export async function openRecent(id: string) {
  const fb = useFeedback.getState()
  fb.setBusy({ label: 'Restoring…' })
  try {
    const rec = await loadSession(id)
    if (!rec) throw new Error('This file is no longer stored on this device.')
    const [primary, ...rest] = rec.state.sources
    const bytes = rec.sourceBytes.get(primary.id)
    if (!bytes) throw new Error('This file is no longer stored on this device.')
    let password: string | undefined
    let incorrect = false
    for (;;) {
      try {
        await useDoc.getState().open({
          bytes,
          name: rec.state.name,
          handle: rec.handle ?? null,
          sessionId: id,
          password,
          restore: {
            pages: rec.state.pages,
            annotations: rec.state.annotations,
            sources: rec.state.sources,
            extraSources: rest
              .map((s) => ({ id: s.id, name: s.name, bytes: rec.sourceBytes.get(s.id)! }))
              .filter((s) => s.bytes),
          },
        })
        break
      } catch (e) {
        if (!(e instanceof PasswordRequiredError)) throw e
        fb.setBusy(null)
        const pw = await fb.askPassword(rec.state.name, incorrect || e.incorrect)
        if (pw === null) {
          useDoc.getState().setStatus('empty')
          return
        }
        password = pw
        incorrect = true
      }
    }
  } catch (e) {
    toast((e as Error).message, 'error')
    useDoc.getState().setStatus('empty')
    await deleteSession(id).catch(() => {})
  } finally {
    fb.setBusy(null)
  }
}

async function rasterize(page: PageRef): Promise<RasterPage> {
  const src = getSource(page.srcId)
  if (!src) throw new Error('Missing source document')
  const p = await src.pdf.getPage(page.srcIndex + 1)
  const scale = Math.min(2.5, 4000 / Math.max(page.width, page.height))
  const vp = p.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(vp.width)
  canvas.height = Math.ceil(vp.height)
  await p.render({ canvas, viewport: vp, annotationMode: 2 /* ENABLE_FORMS */ }).promise
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode failed'))), 'image/jpeg', 0.92),
  )
  canvas.width = 0
  return { bytes: new Uint8Array(await blob.arrayBuffer()), type: 'jpg' }
}

/** Current bytes of a source, including any filled-in form fields. */
export async function sourceBytesWithForms(id: string): Promise<Uint8Array> {
  const src = getSource(id)
  if (!src) throw new Error('Missing source')
  if (src.pdf.annotationStorage.size > 0) {
    try {
      return await src.pdf.saveDocument()
    } catch (e) {
      console.warn('Could not apply form values', e)
    }
  }
  return src.bytes
}

/** Bake the current document (or a subset of pages) into PDF bytes. */
export async function buildPdf(pageIds?: string[]): Promise<Uint8Array> {
  const doc = useDoc.getState()
  const pages = pageIds ? doc.pages.filter((p) => pageIds.includes(p.id)) : doc.pages
  const sources = new Map<string, { bytes: Uint8Array }>()
  for (const s of allSources()) sources.set(s.info.id, { bytes: await sourceBytesWithForms(s.info.id) })
  const fb = useFeedback.getState()
  const { exportPdf } = await import('./pdf/export')
  return exportPdf({
    sources,
    pages,
    annotations: doc.annotations,
    title: doc.fileName.replace(/\.pdf$/i, ''),
    author: useUi.getState().author || undefined,
    rasterize,
    onProgress: (done, total) => {
      if (total > 20) fb.setBusy({ label: 'Saving…', progress: done / total })
    },
  })
}

async function withBusy<T>(label: string, fn: () => Promise<T>): Promise<T | undefined> {
  const fb = useFeedback.getState()
  fb.setBusy({ label })
  try {
    return await fn()
  } catch (e) {
    console.error(e)
    toast(`${label.replace('…', '')} failed: ${(e as Error).message}`, 'error')
    return undefined
  } finally {
    fb.setBusy(null)
  }
}

export async function save() {
  const doc = useDoc.getState()
  if (doc.status !== 'ready') return
  const handle = doc.fileHandle
  if (!handle) return saveAs()
  await withBusy('Saving…', async () => {
    const bytes = await buildPdf()
    if (!(await verifyWritePermission(handle))) throw new Error('Permission to write the file was denied')
    await writeToHandle(handle, bytes)
    useDoc.getState().markSaved()
    toast(`Saved ${handle.name}`, 'success')
  })
}

export async function saveAs() {
  const doc = useDoc.getState()
  if (doc.status !== 'ready') return
  const bytes = await withBusy('Saving…', () => buildPdf())
  if (!bytes) return
  const name = ensurePdfName(doc.fileName)
  const result = await saveAsFile(bytes, name)
  if (result === 'cancelled') return
  if (result) {
    useDoc.getState().setFileHandle(result)
    useDoc.getState().rename(result.name)
    if (doc.sessionId) await storeHandle(doc.sessionId, result)
    toast(`Saved ${result.name}`, 'success')
  } else toast(`Downloaded ${name}`, 'success')
  useDoc.getState().markSaved()
}

export async function downloadCopy() {
  const doc = useDoc.getState()
  const bytes = await withBusy('Preparing download…', () => buildPdf())
  if (!bytes) return
  download(bytes, ensurePdfName(doc.fileName))
  useDoc.getState().markSaved()
}

export async function printDocument() {
  const bytes = await withBusy('Preparing to print…', () => buildPdf())
  if (bytes) printPdf(bytes)
}

export async function shareDocument() {
  const doc = useDoc.getState()
  const bytes = await withBusy('Preparing…', () => buildPdf())
  if (!bytes) return
  if (canShareFiles()) {
    try {
      await shareFile(bytes, ensurePdfName(doc.fileName))
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast('Sharing failed', 'error')
    }
  } else download(bytes, ensurePdfName(doc.fileName))
}

export async function extractPages(pageIds: string[]) {
  const doc = useDoc.getState()
  if (!pageIds.length) return
  const bytes = await withBusy('Extracting pages…', () => buildPdf(pageIds))
  if (!bytes) return
  const name = withSuffix(doc.fileName, pageIds.length === 1 ? `-page` : `-${pageIds.length}-pages`)
  const r = await saveAsFile(bytes, name)
  if (r !== 'cancelled') toast(`Extracted ${pageIds.length} page${pageIds.length > 1 ? 's' : ''}`, 'success')
}

export async function mergeFiles(index?: number) {
  const files = await pickPdfFiles(true)
  if (!files.length) return
  const doc = useDoc.getState()
  let at = index ?? doc.pages.length
  let total = 0
  await withBusy('Adding pages…', async () => {
    for (const f of files) {
      const n = await useDoc.getState().insertFile(f.bytes, f.name, at)
      at += n
      total += n
      const sid = useDoc.getState().sessionId
      const src = allSources().at(-1)
      if (sid && src) await storeSourceBytes(sid, src.info.id, src.bytes)
    }
  })
  if (total) toast(`Added ${total} page${total > 1 ? 's' : ''}`, 'success')
}

export async function closeDocument() {
  await useDoc.getState().close()
  useUi.getState().select([])
  useUi.getState().setOrganize(false)
}
